import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Table } from 'app-components';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { TXN_REASONS_TEXT } from '@constants/enrollmentList/enrollmentList.constant';
import { usePermissions } from '@hooks/usePermissions';
import { DATE_FORMATS, formatDateString } from '@utils/dates/dateMethods';

import { logError } from '../../../../utils/tools/ErrorLoggers';
import { IEnrollmentHistoryItem, IEnrollmentSnapshotResponse, IRootState } from '../common/types';
import useEnrollmentModal from '../hooks/useEnrollmentModal';
import useEnrollmentModalAPI from '../hooks/useEnrollmentModalAPI';

const StyledSection = styled.section`
    margin: 20px 0;
    table {
        margin: 0;
        text-align: left !important;
    }
    thead tr th {
        text-align: left;
    }
    thead {
        display: block;
        overflow-y: scroll;
        scrollbar-color: transparent transparent;
    }
    thead::-webkit-scrollbar-thumb,
    thead::-webkit-scrollbar-track {
        background: transparent;
    }
    thead tr,
    tbody tr {
        display: table;
        width: 100%;
        table-layout: fixed;
    }
    tbody {
        display: block;
        max-height: 150px;
        overflow-y: scroll;
    }
`;

const StyledHeading = styled.h4`
    margin: 1rem 0;
`;

const StyledButton = styled.button`
    color:  ${({ theme }) => theme.primaryMain};
    background: none;
    text-align: left;
    text-transform:none !important;
    font-weight: 600 !important;
    border:none !important;
`;

interface IEventHistoryTableProps {
    enrollmentHistoryList: IEnrollmentHistoryItem[];
    heading: string;
    enrollmentId: number;
}

const EventHistoryTable: React.FC<IEventHistoryTableProps> = ({
    enrollmentHistoryList = [],
    heading,
    enrollmentId,
}) => {
    const intl = useIntl();
    const loggedInUserRole = useSelector((state: IRootState) => state.common.environment.user.role);
    const permissions = useSelector((state: IRootState) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};

    const { handleCancelTerminate, premiumHistory } = useEnrollmentModal();
    const { getEnrollmentSnapshot } = useEnrollmentModalAPI();

    const handleCancelTerminateClick = useCallback(
        async (enrollmentHistory: IEnrollmentHistoryItem) => {
            try {
                const payload = {
                    enrollmentId,
                    subscriberEventId: enrollmentHistory.subscriberEventId,
                };
                const enrollmentSnapshot: IEnrollmentSnapshotResponse = await getEnrollmentSnapshot(payload);
                handleCancelTerminate(enrollmentSnapshot, premiumHistory);
            } catch (error: unknown) {
                logError('Failed to load enrollment snapshot for cancellation:', { error });
            }
        },
        [enrollmentId, getEnrollmentSnapshot, premiumHistory, handleCancelTerminate],
    );

    const { isAgencyUser } = usePermissions();

    const agencyUser = useMemo(() => isAgencyUser(loggedInUserRole), [loggedInUserRole]);
    const canViewPremiumHistory = userPermissions?.memberEnrlmntPrmHist && !agencyUser;

    const getEventHistoryList = [...enrollmentHistoryList].reverse();

    const columns = useMemo(
        () => [
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.time' }),
                key: 'eventCreationDate',
                render: (data: IEnrollmentHistoryItem) => (
                    <>{formatDateString(data.eventCreationDate, DATE_FORMATS.MM_DD_YYYY_H_MM_SS)}</>
                ),
            },
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.event' }),
                key: 'enrollmentEvent',
                render: (data: IEnrollmentHistoryItem) => {
                    return canViewPremiumHistory ? (
                        <StyledButton
                            type="button"
                            onClick={() => handleCancelTerminateClick(data)}
                            className="color-link"
                            aria-label={`View details for ${data.enrollmentEvent}`}
                        >
                            {data.enrollmentEvent}
                        </StyledButton>
                    ) : (
                        <span>{data.enrollmentEvent}</span>
                    );
                },
            },
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.maintenanceReason' }),
                key: 'enrollmentEventReason',
            },
            {
                titleId: intl.formatMessage({
                    id: 'member.enrollment_list.premiumhistory.enrollmentEventReasonDescription',
                }),
                key: 'enrollmentEventReasonDescription',
                render: (data: IEnrollmentHistoryItem) => (
                    <ConditionalRendering
                        isTrue={
                            data.enrollmentEventReason === TXN_REASONS_TEXT.NO_REASON_GIVEN &&
                            !!data.enrollmentEventReasonDescription
                        }
                    >
                        {data.enrollmentEventReasonDescription}
                    </ConditionalRendering>
                ),
            },
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.createdBy' }),
                key: 'eventCreatedBy',
                render: (data: IEnrollmentHistoryItem) => (
                    <>
                        <ConditionalRendering isTrue={agencyUser}>{data.userRole}</ConditionalRendering>
                        <ConditionalRendering isTrue={!agencyUser}>
                            {data.eventCreatedBy} ({data.userRole})
                        </ConditionalRendering>
                    </>
                ),
            },
        ],
        [intl, canViewPremiumHistory, agencyUser, handleCancelTerminateClick],
    );

    return (
        <StyledSection>
            <StyledHeading>{intl.formatMessage({ id: heading })}</StyledHeading>
            <Table columns={columns} data={getEventHistoryList} classNamesStr="enrollment_history" />
        </StyledSection>
    );
};

export default EventHistoryTable;
